import { useMemo, useRef } from 'react';

import KpiRangeSliderInputs from 'components/molecules/PackArchitecture/KpiRangeSliderInputs';
import KpiRangeSliderTrack from 'components/molecules/PackArchitecture/KpiRangeSliderTrack';
import type { BinningMethod } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { getSliderLayout } from 'components/molecules/PackArchitecture/kpiRangeSlider.utils';
import type { KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { useKpiBreakpointDrag } from 'components/molecules/PackArchitecture/useKpiBreakpointDrag';
import { useKpiRangeDrag } from 'components/molecules/PackArchitecture/useKpiRangeDrag';

type KpiRangeSliderProps = {
    dataRange: KpiDataRange;
    min: number;
    max: number;
    binningMethod: BinningMethod;
    breakpoints: number[];
    minInputLabel: string;
    maxInputLabel: string;
    minInputError: string | null;
    maxInputError: string | null;
    onRangeChange: (min: number, max: number) => void;
    onMinInput: (value: number) => void;
    onMaxInput: (value: number) => void;
    onBreakpointsChange?: (breakpoints: number[]) => void;
};

const KpiRangeSlider = ({
    dataRange,
    min,
    max,
    binningMethod,
    breakpoints,
    minInputLabel,
    maxInputLabel,
    minInputError,
    maxInputError,
    onRangeChange,
    onMinInput,
    onMaxInput,
    onBreakpointsChange,
}: KpiRangeSliderProps) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const isCustomBinning = binningMethod === 'custom';
    const sortedBreakpoints = useMemo(
        () => [...breakpoints].sort((left, right) => left - right),
        [breakpoints],
    );

    const { activeHandle, startDrag } = useKpiRangeDrag({
        trackRef,
        dataRange,
        min,
        max,
        onRangeChange,
    });
    const { activeBreakpointIndex, startBreakpointDrag } = useKpiBreakpointDrag({
        trackRef,
        dataRange,
        min,
        max,
        breakpoints: sortedBreakpoints,
        onBreakpointsChange: onBreakpointsChange ?? (() => undefined),
    });
    const layout = getSliderLayout(dataRange, min, max, sortedBreakpoints, isCustomBinning);

    return (
        <div className="flex select-none flex-col gap-4">
            <div className="relative">
                <KpiRangeSliderTrack
                    dataRange={dataRange}
                    min={min}
                    trackRef={trackRef}
                    activeHandle={activeHandle}
                    onStartDrag={startDrag}
                    draggableBreakpoints={isCustomBinning ? sortedBreakpoints : undefined}
                    activeBreakpointIndex={isCustomBinning ? activeBreakpointIndex : null}
                    onStartBreakpointDrag={isCustomBinning ? startBreakpointDrag : undefined}
                    isCustomBinning={isCustomBinning}
                    {...layout}
                />
            </div>
            <KpiRangeSliderInputs
                min={min}
                max={max}
                minInputLabel={minInputLabel}
                maxInputLabel={maxInputLabel}
                minInputError={minInputError}
                maxInputError={maxInputError}
                onMinInput={onMinInput}
                onMaxInput={onMaxInput}
            />
        </div>
    );
};

export default KpiRangeSlider;
